import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { downloadBlob, signFileUrl } from '@/api/client';

/** Resolve a stored file ref (R2 key) to a short-lived viewable URL. data:/http values pass through. */
export function useSignedUrl(ref?: string | null): string {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let alive = true;
    if (!ref) { setUrl(''); return; }
    signFileUrl(ref).then((u) => { if (alive) setUrl(u); }).catch(() => { if (alive) setUrl(''); });
    return () => { alive = false; };
  }, [ref]);
  return url;
}

/** <img> that resolves an R2 ref (or renders a data:/http src directly). */
export function SignedImage({ src, fallback = null, ...props }: { src?: string | null; fallback?: React.ReactNode } & React.ImgHTMLAttributes<HTMLImageElement>) {
  const url = useSignedUrl(src);
  if (!url) return <>{fallback}</>;
  return <img src={url} {...props} />;
}

/**
 * Link to an R2-stored file.
 *
 * With `download`, the file is SAVED rather than opened, and the bytes come from our own API rather
 * than the storage bucket. That indirection is the whole point: a presigned R2 URL carries no
 * content-disposition, and the HTML `download` attribute is ignored cross-origin, so a direct link
 * can only ever open the image in a tab and leave the user to save it by hand.
 */
export function SignedFileLink({ href, children, className, title, download, downloadName }: {
  href?: string | null; children: React.ReactNode; className?: string; title?: string;
  download?: boolean; downloadName?: string;
}) {
  const url = useSignedUrl(href);
  const [busy, setBusy] = useState(false);

  if (download) {
    return (
      <button
        type="button"
        className={className}
        title={title}
        disabled={!href || busy}
        onClick={async () => {
          if (!href) return;
          setBusy(true);
          try {
            await downloadBlob(`/files/download?ref=${encodeURIComponent(href)}&name=${encodeURIComponent(downloadName ?? 'file')}`, downloadName ?? 'file');
          } catch {
            toast.error('Could not download that file.');
          } finally {
            setBusy(false);
          }
        }}>
        {children}
      </button>
    );
  }

  return (
    <a href={url || '#'} target="_blank" rel="noreferrer" className={className} title={title}
      onClick={(e) => { if (!url) e.preventDefault(); }}>
      {children}
    </a>
  );
}
